import React, { useState } from 'react';
import api from '../services/api';
import './PartnerSection.css';

type PartnerType = 'labour' | 'material' | 'architect' | 'builder';

interface FormState {
  name: string;
  contactPerson: string;
  mobile: string;
  email: string;
  address: string;
  category: string;
  aadharNumber: string;
  aadharPhoto: string;
  selfiePhoto: string;
  shopRCPhoto: string;
  companyRCPhoto: string;
  gstNumber: string;
  bankAccount: string;
  ifsc: string;
  upiId: string;
  about: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  contactPerson: '',
  mobile: '',
  email: '',
  address: '',
  category: '',
  aadharNumber: '',
  aadharPhoto: '',
  selfiePhoto: '',
  shopRCPhoto: '',
  companyRCPhoto: '',
  gstNumber: '',
  bankAccount: '',
  ifsc: '',
  upiId: '',
  about: '',
};

const LABOUR_CATEGORIES = [
  'Mason', 'Carpenter', 'Electrician', 'Plumber', 'Painter',
  'Tiles Works', 'Interior Furniture Fitter', 'Welder', 'Helper', 'Other',
];

const MATERIAL_CATEGORIES = [
  'Paint', 'Hardware', 'Plumbing', 'Wiring / Electrical',
  'Wood Works', 'Interior Furniture', 'Tiles', 'Sanitary', 'Cement & Steel', 'Other',
];

const MAX_FILE_BYTES = 3 * 1024 * 1024; // 3 MB per image

const readFileAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });

const PartnerSection: React.FC = () => {
  const [partnerType, setPartnerType] = useState<PartnerType>('labour');
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const update = (patch: Partial<FormState>) => setForm(f => ({ ...f, ...patch }));

  const handleFile =
    (field: keyof FormState) =>
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file');
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        setError('File is too large. Please keep each image under 3 MB.');
        return;
      }
      setError('');
      const dataUrl = await readFileAsDataURL(file);
      update({ [field]: dataUrl } as Partial<FormState>);
    };

  const switchType = (t: PartnerType) => {
    setPartnerType(t);
    setForm(EMPTY_FORM);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.mobile.trim()) {
      setError('Name and mobile are required.');
      return;
    }
    if (form.mobile.replace(/\D/g, '').length < 10) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }
    if ((partnerType === 'labour' || partnerType === 'material') && !form.category) {
      setError('Please select a category.');
      return;
    }

    setSubmitting(true);
    try {
      const payload: any = {
        partnerType,
        name: form.name,
        mobile: form.mobile,
        email: form.email || undefined,
        address: form.address || undefined,
        about: form.about || undefined,
        bankAccount: form.bankAccount || undefined,
        ifsc: form.ifsc || undefined,
        upiId: form.upiId || undefined,
      };

      if (partnerType === 'labour') {
        Object.assign(payload, {
          category: form.category,
          aadharNumber: form.aadharNumber || undefined,
          aadharPhoto: form.aadharPhoto || undefined,
          selfiePhoto: form.selfiePhoto || undefined,
        });
      } else if (partnerType === 'material') {
        Object.assign(payload, {
          category: form.category,
          aadharNumber: form.aadharNumber || undefined,
          aadharPhoto: form.aadharPhoto || undefined,
          shopRCPhoto: form.shopRCPhoto || undefined,
          gstNumber: form.gstNumber || undefined,
        });
      } else {
        // architect or builder
        Object.assign(payload, {
          contactPerson: form.contactPerson || undefined,
          companyRCPhoto: form.companyRCPhoto || undefined,
          gstNumber: form.gstNumber || undefined,
        });
      }

      await api.post('/partners', payload);
      setSubmitted(true);
      setForm(EMPTY_FORM);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const typeLabels: Record<PartnerType, { title: string; intro: string; nameLabel: string }> = {
    labour: {
      title: 'Labour / Service Provider',
      intro: 'Masons, carpenters, electricians, plumbers, painters and more.',
      nameLabel: 'Full Name',
    },
    material: {
      title: 'Material Vendor',
      intro: 'Shops supplying paint, hardware, plumbing, wiring, wood works, furniture and more.',
      nameLabel: 'Shop / Business Name',
    },
    architect: {
      title: 'Architect (Partner Firm)',
      intro: 'Independent architecture firms looking to collaborate.',
      nameLabel: 'Company Name',
    },
    builder: {
      title: 'Civil Construction Builder',
      intro: 'Construction companies looking to join our partner network.',
      nameLabel: 'Company Name',
    },
  };

  const label = typeLabels[partnerType];
  const isLabour = partnerType === 'labour';
  const isMaterial = partnerType === 'material';
  const isCompany = partnerType === 'architect' || partnerType === 'builder';

  return (
    <section id="partner" className="pt-section lp-section">
      <div className="lp-container">
        <div className="lp-section__header">
          <p className="lp-section__tag">Partner with Us</p>
          <h2 className="lp-section__title">Join Our Network</h2>
          <p className="lp-section__sub">
            We source skilled labour, material vendors, architects and builders for our own projects and
            supply manpower to other firms. Apply below and our team will get in touch.
          </p>
        </div>

        {submitted ? (
          <div className="pt-success">
            <span>✅</span>
            <h3>Application Submitted</h3>
            <p>Thank you! Our team will review your details and contact you shortly.</p>
            <button type="button" className="lp-btn lp-btn--primary" onClick={() => setSubmitted(false)}>
              Submit Another
            </button>
          </div>
        ) : (
          <div className="pt-card">
            <div className="pt-tabs" role="tablist" aria-label="Partner type">
              {(['labour', 'material', 'architect', 'builder'] as PartnerType[]).map(t => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={partnerType === t}
                  className={partnerType === t ? 'pt-tab pt-tab--active' : 'pt-tab'}
                  onClick={() => switchType(t)}
                >
                  {typeLabels[t].title}
                </button>
              ))}
            </div>

            <p className="pt-intro">{label.intro}</p>

            {error && <div className="pt-error">{error}</div>}

            <form className="pt-form" onSubmit={handleSubmit}>
              <div className="pt-grid">
                <label className="pt-field">
                  <span>{label.nameLabel} *</span>
                  <input
                    required
                    value={form.name}
                    onChange={e => update({ name: e.target.value })}
                    placeholder={label.nameLabel}
                  />
                </label>

                {isCompany && (
                  <label className="pt-field">
                    <span>Contact Person</span>
                    <input
                      value={form.contactPerson}
                      onChange={e => update({ contactPerson: e.target.value })}
                      placeholder="Name of person we should speak to"
                    />
                  </label>
                )}

                <label className="pt-field">
                  <span>Mobile *</span>
                  <input
                    required
                    inputMode="numeric"
                    maxLength={10}
                    value={form.mobile}
                    onChange={e => update({ mobile: e.target.value.replace(/\D/g, '') })}
                    placeholder="10-digit mobile number"
                  />
                </label>

                <label className="pt-field">
                  <span>Email</span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => update({ email: e.target.value })}
                    placeholder="your@email.com"
                  />
                </label>

                {(isLabour || isMaterial) && (
                  <label className="pt-field">
                    <span>Category *</span>
                    <select
                      required
                      value={form.category}
                      onChange={e => update({ category: e.target.value })}
                    >
                      <option value="">Select category</option>
                      {(isLabour ? LABOUR_CATEGORIES : MATERIAL_CATEGORIES).map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </label>
                )}

                <label className="pt-field pt-field--full">
                  <span>{isMaterial ? 'Shop Address' : isCompany ? 'Office Address' : 'Address'}</span>
                  <textarea
                    rows={2}
                    value={form.address}
                    onChange={e => update({ address: e.target.value })}
                    placeholder="Street, city, pincode"
                  />
                </label>
              </div>

              {/* KYC / documents */}
              <h4 className="pt-group-title">Identity & Documents</h4>
              <div className="pt-grid">
                {(isLabour || isMaterial) && (
                  <>
                    <label className="pt-field">
                      <span>Aadhar Number</span>
                      <input
                        value={form.aadharNumber}
                        onChange={e => update({ aadharNumber: e.target.value.replace(/\D/g, '').slice(0, 12) })}
                        placeholder="12-digit Aadhar number"
                        inputMode="numeric"
                      />
                    </label>
                    <label className="pt-field pt-field--file">
                      <span>Aadhar Photo</span>
                      <input type="file" accept="image/*" onChange={handleFile('aadharPhoto')} />
                      {form.aadharPhoto && <img alt="Aadhar preview" className="pt-preview" src={form.aadharPhoto} />}
                    </label>
                  </>
                )}

                {isLabour && (
                  <label className="pt-field pt-field--file">
                    <span>Selfie</span>
                    <input type="file" accept="image/*" onChange={handleFile('selfiePhoto')} />
                    {form.selfiePhoto && <img alt="Selfie preview" className="pt-preview" src={form.selfiePhoto} />}
                  </label>
                )}

                {isMaterial && (
                  <>
                    <label className="pt-field pt-field--file">
                      <span>Shop RC (Registration Certificate)</span>
                      <input type="file" accept="image/*" onChange={handleFile('shopRCPhoto')} />
                      {form.shopRCPhoto && <img alt="Shop RC preview" className="pt-preview" src={form.shopRCPhoto} />}
                    </label>
                    <label className="pt-field">
                      <span>GST Number (optional)</span>
                      <input
                        value={form.gstNumber}
                        onChange={e => update({ gstNumber: e.target.value.toUpperCase() })}
                        placeholder="GSTIN"
                      />
                    </label>
                  </>
                )}

                {isCompany && (
                  <>
                    <label className="pt-field pt-field--file">
                      <span>Company RC (Registration Certificate)</span>
                      <input type="file" accept="image/*" onChange={handleFile('companyRCPhoto')} />
                      {form.companyRCPhoto && <img alt="Company RC preview" className="pt-preview" src={form.companyRCPhoto} />}
                    </label>
                    <label className="pt-field">
                      <span>GST Number (optional)</span>
                      <input
                        value={form.gstNumber}
                        onChange={e => update({ gstNumber: e.target.value.toUpperCase() })}
                        placeholder="GSTIN"
                      />
                    </label>
                  </>
                )}
              </div>

              {/* Payment details */}
              <h4 className="pt-group-title">Payment Details</h4>
              <div className="pt-grid">
                <label className="pt-field">
                  <span>Bank Account Number</span>
                  <input
                    value={form.bankAccount}
                    onChange={e => update({ bankAccount: e.target.value })}
                    placeholder="Account number"
                  />
                </label>
                <label className="pt-field">
                  <span>IFSC Code</span>
                  <input
                    value={form.ifsc}
                    onChange={e => update({ ifsc: e.target.value.toUpperCase() })}
                    placeholder="e.g. HDFC0001234"
                    maxLength={11}
                  />
                </label>
                <label className="pt-field">
                  <span>UPI ID / GPay Number</span>
                  <input
                    value={form.upiId}
                    onChange={e => update({ upiId: e.target.value })}
                    placeholder="name@upi or mobile linked to UPI"
                  />
                </label>
              </div>

              {/* About */}
              <div className="pt-grid">
                <label className="pt-field pt-field--full">
                  <span>{isLabour ? 'About your work / experience' : isMaterial ? 'About your shop' : 'About your firm'}</span>
                  <textarea
                    rows={3}
                    value={form.about}
                    onChange={e => update({ about: e.target.value })}
                    placeholder="A short description…"
                  />
                </label>
              </div>

              <button type="submit" className="lp-btn lp-btn--primary lp-btn--full" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Application →'}
              </button>
              <p className="pt-note">
                We review all applications manually. No account or login is created — our team will contact you directly.
              </p>
            </form>
          </div>
        )}
      </div>
    </section>
  );
};

export default PartnerSection;
